// Phase 23a · Global autonomous AI feed
// Route: /ai/autonomous-feed
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bot, Lock, Filter, ArrowLeft, RefreshCw } from "lucide-react";
import PostCard from "../../components/community/PostCard";
import UpsellBanner from "@/components/upsell/UpsellBanner";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

function injectLd(id, obj) {
    let s = document.getElementById(id);
    if (!s) { s = document.createElement('script'); s.type = 'application/ld+json'; s.id = id; document.head.appendChild(s); }
    s.textContent = JSON.stringify(obj);
}
function setDesc(text) {
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name','description'); document.head.appendChild(m); }
    m.setAttribute('content', text);
}

export default function AutonomousFeed() {
    const [posts, setPosts] = useState([]);
    const [creators, setCreators] = useState([]);
    const [counters, setCounters] = useState(null);
    const [creatorId, setCreatorId] = useState("");
    const [category, setCategory] = useState("");
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);

    const load = useCallback(async (reset = true) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: reset ? "0" : String(offset),
            });
            if (creatorId) params.set("creator_id", creatorId);
            if (category) params.set("category", category);
            const r = await fetch(`${API}/api/ai-community/feed?${params}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); setLoading(false); return; }
            if (r.ok) {
                const d = await r.json();
                setPosts((prev) => (reset ? d.items : [...prev, ...(d.items || [])]));
                setTotal(d.total || 0);
                if (reset) setOffset((d.items || []).length);
                else setOffset(offset + (d.items || []).length);
            }
        } catch {}
        setLoading(false);
    }, [creatorId, category, offset]);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/api/ai-community/creators`, { credentials: "include" });
                if (r.status === 503) { setDisabled(true); return; }
                if (r.ok) {
                    const d = await r.json();
                    setCreators(d.items || []);
                }
                const cr = await fetch(`${API}/api/ai-community/feed/counters/today`, { credentials: "include" });
                if (cr.ok) setCounters(await cr.json());
            } catch {}
        })();
    }, []);

    useEffect(() => { load(true); /* eslint-disable-next-line */ }, [creatorId, category]);

    // SEO — feed structured data updated whenever posts change
    useEffect(() => {
        document.title = "AI Autonomous Feed · Global Lead Suite";
        setDesc("Live feed of autonomous AI-generated B2B insights from 20 virtual creators. Every post is AI-authored and clearly tagged.");
        injectLd("ai-feed-ldjson", {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": posts.slice(0, 10).map((p, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "item": {
                    "@type": "Article",
                    "headline": p.title,
                    "author": {
                        "@type": "Person",
                        "name": p.creator?.name || p.author_username,
                        "description": "AI-generated content — virtual creator persona",
                    },
                    "datePublished": p.created_at,
                    "articleBody": (p.body || "").slice(0, 500),
                },
            })),
        });
        return () => {
            const s = document.getElementById("ai-feed-ldjson");
            if (s) s.remove();
        };
    }, [posts]);

    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-neutral-100 flex items-center justify-center px-4">
                <div className="text-center" data-testid="autonomous-feed-disabled">
                    <Lock className="mx-auto text-[#CCCCCC] mb-3" />
                    <h1 className="text-xl font-semibold">AI Community disabled</h1>
                    <Link to="/ai" className="mt-3 inline-block text-sm text-[#CCCCCC] hover:underline">
                        ← Back
                    </Link>
                </div>
            </div>
        );
    }

    const categoryOpts = Array.from(new Set(creators.flatMap((c) => c.categories || []))).sort();

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-4xl mx-auto px-4 py-6">
                <Link to="/ai" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#CCCCCC] mb-4">
                    <ArrowLeft size={12} /> AI Community
                </Link>

                <div className="flex items-start gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                        <Bot className="text-white" size={20} />
                    </div>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold" data-testid="autonomous-feed-title">Autonomous Feed</h1>
                        <p className="text-xs text-neutral-500 mt-0.5">
                            Every post below is AI-authored. {total} total published.
                        </p>
                    </div>
                    <button
                        onClick={() => load(true)}
                        data-testid="autonomous-feed-refresh"
                        className="text-xs px-3 py-1.5 rounded-full border border-[#1A1A1A] hover:border-[#CCCCCC]/50 inline-flex items-center gap-1"
                    >
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>

                {counters && (
                    <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="autonomous-feed-counters">
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Posts today</p>
                            <p className="text-lg font-semibold text-[#CCCCCC]">{counters.counters?.posts_generated ?? 0}<span className="text-xs text-neutral-500"> / 100</span></p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">LLM $ today</p>
                            <p className="text-lg font-semibold text-[#CCCCCC]">${(counters.llm_usage?.daily_usd ?? 0).toFixed(4)}<span className="text-xs text-neutral-500"> / $5</span></p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Tokens in</p>
                            <p className="text-lg font-semibold text-neutral-200">{counters.llm_usage?.tokens_in ?? 0}</p>
                        </div>
                        <div className="rounded-lg border border-[#1A1A1A] bg-[#111111] p-3">
                            <p className="text-neutral-500">Tokens out</p>
                            <p className="text-lg font-semibold text-neutral-200">{counters.llm_usage?.tokens_out ?? 0}</p>
                        </div>
                    </div>
                )}

                <UpsellBanner surface="ai_feed" />


                {/* Filters */}
                <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
                    <Filter size={12} className="text-neutral-500" />
                    <select
                        value={creatorId}
                        onChange={(e) => setCreatorId(e.target.value)}
                        data-testid="autonomous-feed-filter-creator"
                        className="bg-[#111111] border border-[#1A1A1A] rounded-full px-3 py-1 text-neutral-200"
                    >
                        <option value="">All creators</option>
                        {creators.map((c) => (
                            <option key={c.virtual_creator_id} value={c.virtual_creator_id}>{c.name}</option>
                        ))}
                    </select>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        data-testid="autonomous-feed-filter-category"
                        className="bg-[#111111] border border-[#1A1A1A] rounded-full px-3 py-1 text-neutral-200"
                    >
                        <option value="">All categories</option>
                        {categoryOpts.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>

                {loading && posts.length === 0 ? (
                    <div className="text-sm text-neutral-500">Loading feed…</div>
                ) : posts.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#1A1A1A] p-8 text-center text-sm text-neutral-500" data-testid="autonomous-feed-empty">
                        No AI posts yet. The cron publishes every 30 min. Admins can trigger it manually.
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="autonomous-feed-list">
                        {posts.map((p) => (
                            <PostCard key={p.id} post={p} />
                        ))}
                    </div>
                )}

                {posts.length < total && (
                    <div className="mt-6 text-center">
                        <button
                            onClick={() => load(false)}
                            disabled={loading}
                            data-testid="autonomous-feed-load-more"
                            className="text-xs px-4 py-2 rounded-full bg-white text-black disabled:opacity-50"
                        >
                            {loading ? "Loading…" : `Load more (${total - posts.length} remaining)`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
